import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes, type HTMLAttributes, type ReactNode } from 'react';

import { cn } from '../../lib/cn';
import type { StatusTone } from '../../lib/status';

/**
 * The chip ANATOMY, written once. `MetaChip`'s outline and plain-text chips
 * are the same box with a different fill, and a second copy of this string is
 * a second chance for one of them to drift a pixel.
 */
export const chipBase =
  'inline-flex items-center gap-1 rounded-chip px-[7px] py-[3px] text-chip uppercase whitespace-nowrap';

/**
 * THE FIELD-AND-WORD RULE, as a component.
 *
 * A status is a solid field carrying a French word in near-black text. Never a
 * bare dot, never a coloured word alone, never a hue with no word — workers read
 * slowly and some read no English, so the word is the signal and the colour is
 * only the accelerator.
 *
 * The near-black-on-light construction is what makes the chip theme-independent:
 * `#0A0C0D` on `#F0B429` is 10.52:1 whether it lands on the dark phone or the
 * white portal, so the identical chip renders at the identical ratio on both.
 */
const chip = cva(chipBase, {
  variants: {
    tone: {
      success: 'bg-status-success text-status-success-fg',
      warning: 'bg-status-warning text-status-warning-fg',
      danger: 'bg-status-danger text-status-danger-fg',
      info: 'bg-status-info text-status-info-fg',
      neutral: 'bg-status-neutral text-status-neutral-fg',
      /**
       * Terminal states — Verrouillée, Archivé. White on brand slate, the one
       * status field that is not near-black-on-light, because "closed" should
       * not read as "unremarkable".
       */
      'neutral-strong': 'bg-status-neutral-strong text-status-neutral-strong-fg',
    },
  },
  defaultVariants: { tone: 'neutral' },
});

export type StatusChipVariants = VariantProps<typeof chip>;

export interface StatusChipProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  readonly tone: StatusTone;
  /**
   * The localized status noun — *Conforme*, *Bloquant*, *Verrouillée*. Never a
   * code, never an enum name, never an abbreviation. Required on purpose.
   */
  readonly children: ReactNode;
  /** A leading glyph. Decorative: the word is what carries the meaning. */
  readonly icon?: ReactNode;
}

export const StatusChip = forwardRef<HTMLSpanElement, StatusChipProps>(function StatusChip(
  { tone, icon, className, children, ...rest },
  ref,
) {
  return (
    <span {...rest} ref={ref} className={cn(chip({ tone }), className)}>
      {icon ? (
        <span aria-hidden="true" className="inline-flex shrink-0">
          {icon}
        </span>
      ) : null}
      {children}
    </span>
  );
});

export interface ChipButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children' | 'color'
> {
  readonly tone: StatusTone;
  readonly children: ReactNode;
  readonly icon?: ReactNode;
  /**
   * Worker surfaces pad the chip out to a 48 px hit area. Direction C shipped
   * 24–26 px chips that *looked* tappable and were not; this is the opposite
   * failure and the one that matters — a chip that opens a disclosure has to be
   * reachable by a gloved thumb.
   */
  readonly surface?: 'worker' | 'admin';
}

/**
 * A status chip that opens something — the *Ajusté* chip that reveals the
 * adjustment reason, the `+1` chip that opens the row detail.
 *
 * It is a real `<button>`, so it is keyboard reachable and announced as a
 * control. The chip itself keeps its 10 px type; the *target* grows around it.
 */
export const ChipButton = forwardRef<HTMLButtonElement, ChipButtonProps>(function ChipButton(
  { tone, icon, className, children, surface = 'admin', type = 'button', ...rest },
  ref,
) {
  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      className={cn(
        'inline-flex items-center justify-center',
        'cursor-pointer rounded-control bg-transparent',
        'transition-colors dur-state ease-out',
        surface === 'worker' ? 'min-h-touch min-w-touch px-2' : 'min-h-8 min-w-8 px-1',
        'hover:bg-surface-sunk',
        className,
      )}
    >
      <span className={cn(chip({ tone }))}>
        {icon ? (
          <span aria-hidden="true" className="inline-flex shrink-0">
            {icon}
          </span>
        ) : null}
        {children}
      </span>
    </button>
  );
});

export interface QuietStatusProps extends HTMLAttributes<HTMLSpanElement> {
  readonly children: ReactNode;
}

/**
 * The noise floor.
 *
 * A clean row says *Conforme* in muted ink as plain text — not a chip, and not
 * blank. If the clean rows vanish, the eye cannot find the flagged ones; if
 * every row is a chip, every row shouts. This is the component for the 13 of 18
 * rows where nothing is wrong.
 */
export const QuietStatus = forwardRef<HTMLSpanElement, QuietStatusProps>(function QuietStatus(
  { className, children, ...rest },
  ref,
) {
  return (
    <span {...rest} ref={ref} className={cn('text-body-compact text-ink-muted', className)}>
      {children}
    </span>
  );
});
